import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormGetValues } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import {
  QuoteConfirmationStatus,
  updateQuoteRequestSuggestion,
} from 'lib/api/updateQuoteRequestSuggestion';
import { Maybe, Undef } from 'types/UtilityTypes';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import captureException from 'utils/captureException';
import { calculatePricing } from 'utils/priceCalculations';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import {
  CarrierCostType,
  ProfitType,
  QuickQuoteInputs,
  QuoteMode,
} from '../types';

const DEBOUNCE_MS = 600;

export type QuoteConfirmationPingValues = {
  carrierCost: number;
  carrierCostType: CarrierCostType;
  profit: number;
  profitType: ProfitType;
  finalFlatPrice: Maybe<number>;
  maxDistance: number;
};

type GetCustomerName = (path: 'customerName') => string;

export type UseQuoteConfirmationPingWithValuesOptions = {
  readyForDefaultPing?: boolean;
  selectedLtlQuickQuoteIds?: number[];
};

function payloadKey(p: {
  finalQuotePrice: number;
  finalMargin: number;
  marginType: ProfitType;
  finalCarrierCost: number;
  carrierCostType: CarrierCostType;
}) {
  return `${p.finalQuotePrice}|${p.finalMargin}|${p.marginType}|${p.finalCarrierCost}|${p.carrierCostType}`;
}

/**
 * Core hook: sends default ping when quote/calculator first loads, and debounced
 * implied_by_user_edit pings when calculator values change. No context required.
 * Debounce is tied only to calculator value changes; selection/focus changes do
 * not reset the timer. Skips implied ping if payload is unchanged from last send.
 *
 * PATCH calls are fire-and-forget (non-blocking): effects do not await the API.
 */
export function useQuoteConfirmationPingWithValues(
  quoteRequestId: number,
  getValues: UseFormGetValues<QuickQuoteInputs> | GetCustomerName,
  getSelectedQuickQuoteId: () => Undef<number>,
  values: QuoteConfirmationPingValues,
  options?: UseQuoteConfirmationPingWithValuesOptions
) {
  const {
    carrierCost,
    carrierCostType,
    profit,
    profitType,
    finalFlatPrice,
    maxDistance,
  } = values;

  const defaultSentForRequestId = useRef<number>(0);
  const lastSentImpliedKeyRef = useRef<Maybe<string>>(null);
  const readyForDefaultPing = options?.readyForDefaultPing !== false;
  const selectedLtlQuickQuoteIds = options?.selectedLtlQuickQuoteIds;

  const getValuesRef = useRef(getValues);
  const getSelectedQuickQuoteIdRef = useRef(getSelectedQuickQuoteId);
  const selectedLtlQuickQuoteIdsRef = useRef(selectedLtlQuickQuoteIds);
  getValuesRef.current = getValues;
  getSelectedQuickQuoteIdRef.current = getSelectedQuickQuoteId;
  selectedLtlQuickQuoteIdsRef.current = selectedLtlQuickQuoteIds;

  const buildPayload = useCallback(
    (
      g: GetCustomerName | UseFormGetValues<QuickQuoteInputs>,
      quoteConfirmationStatus: QuoteConfirmationStatus
    ) => {
      const { flatCarrierCost: finalFlatCarrierCost, finalProfit } =
        calculatePricing(
          carrierCost,
          carrierCostType,
          profit,
          profitType,
          maxDistance
        );
      return {
        finalQuotePrice: _.round(finalFlatPrice ?? 0),
        finalMargin: _.round(finalProfit),
        marginType: profitType,
        finalCarrierCost: _.round(finalFlatCarrierCost),
        carrierCostType: carrierCostType,
        customerExternalTMSId: g('customerName') as string,
        quoteConfirmationStatus,
      };
    },
    [
      carrierCost,
      carrierCostType,
      profit,
      profitType,
      maxDistance,
      finalFlatPrice,
    ]
  );

  useEffect(() => {
    if (quoteRequestId === 0 || !readyForDefaultPing) return;
    if (defaultSentForRequestId.current !== quoteRequestId) {
      defaultSentForRequestId.current = quoteRequestId;
      lastSentImpliedKeyRef.current = null;
      const payload = buildPayload(
        getValuesRef.current,
        QuoteConfirmationStatus.Default
      );

      Promise.resolve(
        updateQuoteRequestSuggestion(
          quoteRequestId,
          SuggestionStatus.InFlight,
          payload,
          getSelectedQuickQuoteIdRef.current() ?? undefined,
          selectedLtlQuickQuoteIdsRef.current
        )
      ).catch((err: unknown) => {
        captureException(err, {
          functionName: 'useQuoteConfirmationPingWithValues default ping',
        });
      });
      lastSentImpliedKeyRef.current = payloadKey(payload);
    }
  }, [quoteRequestId, readyForDefaultPing, buildPayload]);

  useEffect(() => {
    if (quoteRequestId === 0) return;
    const timer = setTimeout(() => {
      const payload = buildPayload(
        getValuesRef.current,
        QuoteConfirmationStatus.ImpliedByUserEdit
      );

      const key = payloadKey(payload);
      if (lastSentImpliedKeyRef.current === key) return;
      lastSentImpliedKeyRef.current = key;

      Promise.resolve(
        updateQuoteRequestSuggestion(
          quoteRequestId,
          SuggestionStatus.InFlight,
          payload,
          getSelectedQuickQuoteIdRef.current() ?? undefined,
          selectedLtlQuickQuoteIdsRef.current
        )
      ).catch((err: unknown) => {
        captureException(err, {
          functionName: 'useQuoteConfirmationPingWithValues implied ping',
        });
      });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    quoteRequestId,
    carrierCost,
    carrierCostType,
    profit,
    profitType,
    finalFlatPrice,
    maxDistance,
    buildPayload,
  ]);
}

/**
 * Sends default + debounced implied pings using QuickQuoteContext. Use from
 * QuickQuoteForm when FTL or LTL has a valid quote.
 */
export function useQuoteConfirmationPing(
  quoteRequestId: number,
  getValues: UseFormGetValues<QuickQuoteInputs>,
  getSelectedQuickQuoteId: () => Undef<number>
) {
  const {
    carrierCost,
    carrierCostType,
    profit,
    profitType,
    finalFlatPrice,
    maxDistance,
    quoteMode,
    selectedLTLQuotes,
  } = useQuickQuoteContext();

  const selectedLtlQuickQuoteIds = useMemo(() => {
    if (quoteMode !== QuoteMode.LTL) return undefined;
    return selectedLTLQuotes
      .map((q) => {
        const rawId =
          (q as unknown as { id?: number | string }).id ??
          (q as unknown as { ID?: number | string }).ID;
        return Number(rawId);
      })
      .filter((id) => Number.isFinite(id) && id > 0);
  }, [quoteMode, selectedLTLQuotes]);

  useQuoteConfirmationPingWithValues(
    quoteRequestId,
    getValues,
    getSelectedQuickQuoteId,
    {
      carrierCost,
      carrierCostType,
      profit,
      profitType,
      finalFlatPrice,
      maxDistance,
    },
    { readyForDefaultPing: true, selectedLtlQuickQuoteIds }
  );
}
